// "Popular Now" — the Home rail listing the techniques starred most in the
// last week, across everyone. The app's one popularity signal, and it costs
// nobody anything: the star they already tap is the vote. Counts come from
// stats/most_starred, tallied nightly by scheduledStarTally in
// functions/index.js from every user's own status doc.
//
// Hidden until there's a board with something on it: an empty "most starred"
// card would read as an empty app.
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { Star, Flame } from 'lucide-react';
import { db } from '../firebase';
import { useGuest, useGuestStat } from '../guestMode';
import { useTechniques } from '../hooks/useTechniques';
import TechniqueTile from './TechniqueTile';
import Rail from './Rail';
import { TIER_ACCENT_COLORS } from '../constants/layout';
import { groupTechniques } from '../utils/groupTechniques';
import { TechniqueStatus, type BeltRank } from '../types';
import { TechniqueGrid, type LibraryBridge } from './Spotlights';
import { DetailPage } from './Forum';
import { FeedSkeleton, TileRailSkeleton } from './RailSkeleton';

/** How many tiles the rail carries. */
const SHOWN = 10;

interface Board { id: string; stars: number }

export default function MostStarred({ library, feed, open, onOpen, onClose }: {
  library?: LibraryBridge;
  /** Render this many clips into Home's four-across feed instead of the rail. */
  feed?: number;
  /** Render the full-screen grid of the whole board instead of the rail. */
  open?: boolean;
  /** The rail's "See all" — Home flips `open` (it hides the other rails). */
  onOpen?: () => void;
  onClose?: () => void;
}) {
  const { guest, stats } = useGuest();
  const { techniques, loading } = useTechniques({ guest });
  const guestBoard = useGuestStat<{ items?: Board[] }>('mostStarred');
  const [board, setBoard] = useState<Board[]>([]);
  // Whether the board doc has answered yet — an empty board and a board still
  // on its way look the same in `board`, and only one of them gets a skeleton.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Guests read the public copy instead — stats/most_starred needs auth.
    if (guest) return;
    return onSnapshot(doc(db, 'stats', 'most_starred'), (snap) => {
      setBoard(((snap.data()?.items ?? []) as Board[]).filter((b) => b.stars > 0));
      setLoaded(true);
    }, () => { setLoaded(true); /* not signed in, or no board yet */ });
  }, [guest]);

  const effectiveBoard = guest ? (guestBoard?.items ?? []).filter((b) => b.stars > 0) : board;

  // Board ids -> techniques still in the catalog, in board order. A gi/no-gi
  // pair counts as one entry, the way Library shows them.
  const list = useMemo(() => {
    const byId = new Map(techniques.map((t) => [t.id, t]));
    return effectiveBoard.map((b) => byId.get(b.id)).filter((t): t is NonNullable<typeof t> => !!t);
  }, [effectiveBoard, techniques]);
  const starsById = useMemo(
    () => new Map(effectiveBoard.map((b) => [b.id, b.stars])),
    [effectiveBoard],
  );
  const rows = useMemo(
    () => groupTechniques(list).slice(0, SHOWN).map((g) => ({ g, stars: starsById.get(g.lead.id) ?? 0 })),
    [starsById, list],
  );

  // Home's feed: the board in order, flowing down the page rather than off to
  // the right. The counts come with it — the number is the whole point of the
  // section, and it sits in the top corner, clear of the name at the foot.
  if (feed !== undefined) {
    if (loading || (guest ? !stats : !loaded)) return <FeedSkeleton />;
    if (list.length === 0) return <div className="home-feed-empty">Nothing starred yet this week.</div>;
    return (
      <TechniqueGrid list={list.slice(0, feed)} library={library} stars={starsById} className="home-grid" />
    );
  }

  if (open) {
    return (
      <DetailPage title="Popular" icon={<Flame size={17} />} onBack={() => onClose?.()}>
        <div className="sp-scroll">
          <TechniqueGrid list={list} library={library} />
        </div>
      </DetailPage>
    );
  }

  // Either half still missing (the board, or the catalog it names): hold the
  // rail's space rather than popping the strip in under the reader.
  if (loading || (guest ? !stats : !loaded)) {
    return <TileRailSkeleton title="Popular" icon={<Flame size={16} />} />;
  }
  if (rows.length === 0) return null;

  return (
    <Rail title="Popular" icon={<Flame size={16} />} onSeeAll={onOpen} tiles>
      {rows.map(({ g, stars }) => { const t = g.lead; return (
        <div className="rail-cell" key={g.key}>
          <TechniqueTile
            id={t.id}
            name={t.name}
            status={library?.statusOf(t.id) ?? TechniqueStatus.Unchecked}
            categoryColor={TIER_ACCENT_COLORS[t.tier as BeltRank]}
            instagramUrl={t.instagramUrl}
            thumbnail={t.thumbnail}
            watched={library?.isWatched(t) ?? false}
            onPipClick={library ? () => library.onPlay(t) : undefined}
            onToggleMat={library ? () => library.onToggleMat(t) : undefined}
            onInfoClick={library ? () => library.onInfo(t) : undefined}
            variantCount={g.variants.length}
            onVariantsClick={library ? () => library.onVersions(g) : undefined}
            onOpenAllVersions={library ? () => library.onOpenAllVersions(g) : undefined}
          />
          {/* The poll result: how many people starred it this week. */}
          <span className="rail-stars"><Star size={11} fill="currentColor" />{stars}</span>
        </div>
      ); })}
    </Rail>
  );
}
